export type WithSeq = {
  lat: number;
  lng: number;
  sequence?: number | string | null;
  orderId?: string | number; 
  name?: string;
  customerName?: string;
};

export type Cluster = {
  center: { lat: number; lng: number };
  items: WithSeq[];
};

export type MarkerDef = {
  orderIds?: string[];
  pos: { lat: number; lng: number };
  label: string;  
  title: string;   
};

export const DEFAULT_CLUSTER_TOLERANCE_M = 6; 

export function seqVal(x: WithSeq): number {
  const n = Number(x.sequence);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY; 
}

export function routeOnce(
  ds: google.maps.DirectionsService,
  req: google.maps.DirectionsRequest
): Promise<google.maps.DirectionsResult> {
  return new Promise((resolve, reject) => {
    ds.route(req, (res, status) => {
      if (status === "OK" && res) resolve(res);
      else reject(new Error(`Directions failed: ${status}`));
    });
  });
}

export function directionsTotals(result: google.maps.DirectionsResult): { distanceKm: number; drivingMinutes: number } | null {
  const legs = result.routes[0]?.legs;
  if (!legs?.length || legs.some(leg => !leg.distance || !leg.duration)) return null;
  return { distanceKm: legs.reduce((sum, leg) => sum + leg.distance!.value, 0) / 1000,
    drivingMinutes: legs.reduce((sum, leg) => sum + leg.duration!.value, 0) / 60 };
}

export async function drawChainedDirectionsAsPolyline(
  map: google.maps.Map,
  coords: { lat: number; lng: number }[],
  options?: google.maps.PolylineOptions,
  onMetrics?: (totals: { distanceKm: number; drivingMinutes: number } | null) => void
): Promise<google.maps.Polyline> {
  const MAX_LOCATIONS = 25;
  const ds = new google.maps.DirectionsService();

  const polyline = new google.maps.Polyline({
    map,
    geodesic: true,
    strokeColor: "#0d1a47",
    strokeOpacity: 0.9,
    strokeWeight: 5,
    ...options, 
  });
  
  const path = polyline.getPath();
  let distanceKm = 0, drivingMinutes = 0, hasMetrics = true;

  try {
  for (let start = 0; start < coords.length - 1; ) {
    const end = Math.min(start + (MAX_LOCATIONS - 1), coords.length - 1);
    const origin = coords[start];
    const destination = coords[end];
    const waypoints = coords.slice(start + 1, end).map((c) => ({
      location: new google.maps.LatLng(c.lat, c.lng),
      stopover: true,
    }));

    const res = await routeOnce(ds, {
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination: new google.maps.LatLng(destination.lat, destination.lng),
      waypoints,
      optimizeWaypoints: false, 
      travelMode: google.maps.TravelMode.DRIVING,
    });

    const totals = directionsTotals(res);
    if (!totals) hasMetrics = false;
    else { distanceKm += totals.distanceKm; drivingMinutes += totals.drivingMinutes; }
    res.routes[0].overview_path.forEach((p) => path.push(p));
    start = end;
  }
  onMetrics?.(hasMetrics ? { distanceKm, drivingMinutes } : null);
  } catch (error) { polyline.setMap(null); throw error; }
  return polyline;
}

export function distM(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  return google.maps.geometry.spherical.computeDistanceBetween(
    new google.maps.LatLng(a.lat, a.lng),
    new google.maps.LatLng(b.lat, b.lng)
  );
}

export function clusterByDistance(items: WithSeq[], toleranceM = DEFAULT_CLUSTER_TOLERANCE_M): Cluster[] {
  const clusters: Cluster[] = [];
  for (const it of items) {
    const p = { lat: it.lat, lng: it.lng };
    const existing = clusters.find((cl) => distM(cl.center, p) <= toleranceM);
    if (existing) {
      existing.items.push(it);
      const n = existing.items.length;
      existing.center.lat = (existing.center.lat * (n - 1) + p.lat) / n;
      existing.center.lng = (existing.center.lng * (n - 1) + p.lng) / n;
    } else {
      clusters.push({ center: { ...p }, items: [it] });
    }
  }
  return clusters;
}

export function sortClustersBySeq(clusters: Cluster[]): Cluster[] {
  return clusters.slice().sort((a, b) => {
    const amin = Math.min(...a.items.map(seqVal));
    const bmin = Math.min(...b.items.map(seqVal));
    return amin - bmin;
  });
}

function uniqueKey(i: WithSeq): string {
  if (i.orderId !== undefined && i.orderId !== null && `${i.orderId}` !== "") {
    return String(i.orderId);
  }
  const lat = i.lat.toFixed(6);
  const lng = i.lng.toFixed(6);
  const s = Number.isFinite(Number(i.sequence)) ? String(Number(i.sequence)) : "";
  return `${lat},${lng}|${s}`;
}

export function buildMarkerDefsContiguous(clusters: Cluster[]): MarkerDef[] {
  const ordered = sortClustersBySeq(clusters);
  let counter = 1; 
  return ordered.map((cl) => {
    const uniqMap = new Map<string, WithSeq>();
    for (const it of cl.items) {
      const k = uniqueKey(it);
      if (!uniqMap.has(k)) uniqMap.set(k, it);
    }
    const uniqueItems = Array.from(uniqMap.values());
    const U = uniqueItems.length;
    const extra = Math.max(0, U - 1);

    const lines = uniqueItems
      .slice()
      .sort((a, b) => seqVal(a) - seqVal(b))
      .map((i) => {
        const id = (i as any).orderId ?? "";
        const name = (i as any).name ?? (i as any).customerName ?? "";
        return `${id}${name ? `: ${name}` : ""}`.trim();
      });

    const label = extra > 0 ? `${counter}+${extra}` : `${counter}`;
    const def: MarkerDef = {
      orderIds: uniqueItems.filter(item => item.orderId != null).map(item => String(item.orderId)),
      pos: cl.center,
      label,
      title: lines.join("\n"),
    };

    counter += U; 
    return def;
  });
}

